import React, { JSX } from 'react';
import {
    MdChevronLeft,
    MdChevronRight,
} from 'react-icons/md';
import {
    DocumentItem,
    formatDateKey,
    ScheduleEntry,
} from './schedule.types';
import CoordinatorSidebar from './CoordinatorSidebar';

interface ScheduleCalendarProps {
    currentDate: Date;
    setCurrentDate: (d: Date) => void;
    scheduleData: Record<string, ScheduleEntry[]>;
    loading: boolean;
    onDateClick: (dateKey: string, coordinators: ScheduleEntry[]) => void;
    sidebarOpen: boolean;
    selectedDate: string | null;
    selectedCoordinator: ScheduleEntry | null;
    selectedDoc: DocumentItem | null;
    previewLoaded: boolean;
    setPreviewLoaded: (v: boolean) => void;
    setSelectedDoc: (doc: DocumentItem | null) => void;
    onCoordinatorClick: (entry: ScheduleEntry) => void;
    onCloseSidebar: () => void;
    onDownload: (doc: DocumentItem) => void;
}

const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
    currentDate,
    setCurrentDate,
    scheduleData,
    loading,
    onDateClick,
    sidebarOpen,
    selectedDate,
    selectedCoordinator,
    selectedDoc,
    previewLoaded,
    setPreviewLoaded,
    setSelectedDoc,
    onCoordinatorClick,
    onCloseSidebar,
    onDownload,
}) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const today = new Date();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthName = currentDate.toLocaleString('default', { month: 'long' });
    const monthShort = currentDate.toLocaleString('default', { month: 'short' });

    const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
    const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

    const selectedDateCoordinators = selectedDate ? (scheduleData[selectedDate] || []) : [];

    const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;
    const cells: JSX.Element[] = [];

    for (let i = 0; i < totalCells; i++) {
        const day = i - firstDay + 1;
        const isValidDay = day >= 1 && day <= daysInMonth;

        if (!isValidDay) {
            cells.push(
                <div
                    key={`empty-${i}`}
                    className="h-20 sm:h-24 border-r border-b border-slate-100 bg-slate-50/50"
                />
            );
            continue;
        }

        const dateKey = formatDateKey(year, month, day);
        const coordinators = scheduleData[dateKey] || [];
        const isToday =
            day === today.getDate() &&
            month === today.getMonth() &&
            year === today.getFullYear();
        const maxVisible = 2;
        const extraCount = coordinators.length - maxVisible;

        cells.push(
            <div
                key={day}
                onClick={() => onDateClick(dateKey, coordinators)}
                className={`h-20 sm:h-24 border-r border-b border-slate-100 p-1 sm:p-1.5 flex flex-col gap-0.5 transition-colors ${isToday ? 'bg-brand-50/60' : 'bg-white'
                    } ${coordinators.length > 0 ? 'cursor-pointer hover:bg-slate-50' : ''}`}
            >
                <div className="flex justify-center mb-0.5">
                    <span
                        className={`w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full text-[10px] sm:text-[11px] font-semibold ${isToday
                            ? 'bg-brand-700 text-white shadow-md shadow-brand-200'
                            : 'text-slate-600'
                            }`}
                    >
                        {day}
                    </span>
                </div>

                <div className="flex flex-col gap-0.5 flex-1 overflow-hidden min-h-0">
                    {coordinators.slice(0, maxVisible).map((item) => (
                        <div
                            key={item.id}
                            className="bg-brand-700 text-white rounded font-medium overflow-hidden flex-shrink-0"
                            style={{
                                fontSize: '9px',
                                lineHeight: '14px',
                                padding: '1px 3px',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                            }}
                            title={`${item.coordinator.firstname} ${item.coordinator.lastname}`}
                        >
                            <span className="lg:hidden">{item.coordinator.firstname}</span>
                            <span className="hidden lg:inline">
                                {item.coordinator.firstname} {item.coordinator.lastname}
                            </span>
                        </div>
                    ))}
                    {extraCount > 0 && (
                        <span
                            className="text-brand-600 font-semibold flex-shrink-0"
                            style={{ fontSize: '9px', paddingLeft: '2px' }}
                        >
                            +{extraCount} more
                        </span>
                    )}
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden h-[85vh]">
                {/* Responsive header */}
                <div className="flex items-center justify-between gap-2 px-2 sm:px-5 py-3 sm:py-4 border-b border-slate-100">
                    <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1">
                        <button
                            onClick={prevMonth}
                            className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 hover:text-brand-700 transition-colors"
                            aria-label="Previous month"
                        >
                            <MdChevronLeft size={20} />
                        </button>
                        <h2 className="text-sm sm:text-base font-semibold text-slate-800 flex-1 sm:flex-none sm:w-40 text-center truncate">
                            <span className="sm:hidden">{monthShort} {year}</span>
                            <span className="hidden sm:inline">{monthName} {year}</span>
                        </h2>
                        <button
                            onClick={nextMonth}
                            className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 hover:text-brand-700 transition-colors"
                            aria-label="Next month"
                        >
                            <MdChevronRight size={20} />
                        </button>
                    </div>
                    <button
                        onClick={() => setCurrentDate(new Date())}
                        className="shrink-0 text-xs sm:text-sm font-medium text-brand-700 hover:text-brand-800 px-2.5 sm:px-3 py-1.5 bg-brand-50 hover:bg-brand-100 rounded-lg transition-colors"
                    >
                        Today
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center h-80">
                        <div className="w-8 h-8 border-4 border-brand-700 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <div className="min-w-[480px]">
                            <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-100">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                                    <div
                                        key={d}
                                        className="py-2.5 text-center text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider"
                                    >
                                        {d}
                                    </div>
                                ))}
                            </div>
                            <div className="grid grid-cols-7 border-t border-l border-slate-100">{cells}</div>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex items-center gap-4 text-xs text-slate-500 mt-3 px-1">
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-brand-50 border border-brand-200" />
                    <span>Today</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-brand-700" />
                    <span>Coordinator assigned</span>
                </div>
            </div>

            {/* Sidebar — rendered via portal in document.body, no parent stacking issues */}
            <CoordinatorSidebar
                open={sidebarOpen}
                selectedDate={selectedDate}
                coordinators={selectedDateCoordinators}
                selectedCoordinator={selectedCoordinator}
                selectedDoc={selectedDoc}
                previewLoaded={previewLoaded}
                setPreviewLoaded={setPreviewLoaded}
                setSelectedDoc={setSelectedDoc}
                onCoordinatorClick={onCoordinatorClick}
                onClose={onCloseSidebar}
                onDownload={onDownload}
            />
        </>
    );
};

export default ScheduleCalendar;